
import { defineComponent, ref, onMounted, computed, reactive } from 'vue';
import { useStore } from 'vuex';
import { POST_MERCHANDISE, PUT_MERCHANDISE } from "@/store/merchandise.module";
import { POST_IMAGE } from "@/store/upload.module";
import { showError } from '@/utils/swal';
import ApiService from '@/store/api.service';
import AppSelect from '@/components/input/AppSelect.vue';
import type { ApiDataResponse, ApiErrorResponse, MerchandisePayload } from '@/types/domain';

type CategoryResponse = ApiDataResponse<string[]> | string[];

const MAX_IMAGE_SIZE_MB = 5;
const MAX_IMAGE_SIZE_BYTES = MAX_IMAGE_SIZE_MB * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = new Set(['image/jpeg', 'image/png']);

export default defineComponent({
  components: {
    AppSelect,
  },
  props: {
    title: {
      type: String,
      required: true
    },
    id:{
      type: String,
      required: false
    },
    data: {
      type: Object,
      required: false,
      default: () => ({}) // Provide a default object to prevent undefined errors
    }
  },
  setup(props, { emit }) {
    const store = useStore();
    const initialData = props.data as MerchandisePayload;
    const modalContent = ref(null);
    const isLoading = ref(false);
    const formData = reactive({
      id: '',
      data: { ...initialData } as MerchandisePayload,
    });

    const kategoriOptions = ref<string[]>(['Stiker', 'Busana', 'ATK']);
    const selectedKategori = ref<string>(String(initialData.kategori || ''));
    const showNewKategori = ref(false);
    const newKategoriInput = ref('');
    const imageMode = ref<'url' | 'upload'>(typeof initialData.image === 'string' && initialData.image ? 'url' : 'upload');
    const imageUrl = ref(typeof initialData.image === 'string' ? initialData.image : '');
    const imagePreview = ref(typeof initialData.image === 'string' ? initialData.image : '');
    const kategoriSelectOptions = computed(() => [
      { value: '', label: 'Pilih Kategori' },
      ...kategoriOptions.value.map((kategori) => ({ value: kategori, label: kategori })),
      { value: '__new__', label: 'Tambah Kategori Baru' },
    ]);

    onMounted(async () => {
      try {
        const res = await ApiService.get<CategoryResponse>('/merchandises/categories');
        const categories = Array.isArray(res) ? res : res.data;
        if (Array.isArray(categories)) kategoriOptions.value = categories;
      } catch { /* fallback ke default */ }
    });

    const onKategoriChange = (value: string | number | boolean | null | undefined) => {
      const val = String(value || '');
      if (val === '__new__') {
        showNewKategori.value = true;
        selectedKategori.value = '__new__';
        formData.data['kategori'] = '';
      } else {
        showNewKategori.value = false;
        newKategoriInput.value = '';
        selectedKategori.value = val;
        formData.data['kategori'] = val || '';
      }
    };

    const closeModal = () => {
      isLoading.value = false;
      emit('close');
    };

    const updateValue = (params: { key: string; value: unknown }) => {
      formData.data[params.key] = params.value;
    };

    const formatCurrencyInput = (value: unknown) => {
      const numberValue = Number(String(value || '').replace(/\D/g, ''));
      if (!numberValue) return '';
      return new Intl.NumberFormat('id-ID', {
        style: 'currency',
        currency: 'IDR',
        minimumFractionDigits: 0,
      }).format(numberValue);
    };

    const onPriceInput = (event: Event) => {
      const input = event.target as HTMLInputElement;
      const rawValue = input.value.replace(/\D/g, '');
      formData.data.price = rawValue ? Number(rawValue) : '';
      input.value = formatCurrencyInput(formData.data.price);
    };

    const onHppInput = (event: Event) => {
      const input = event.target as HTMLInputElement;
      const rawValue = input.value.replace(/\D/g, '');
      formData.data.hpp = rawValue ? Number(rawValue) : '';
      input.value = formatCurrencyInput(formData.data.hpp);
    };

    const onImageUrlInput = () => {
      formData.data.image = imageUrl.value;
      imagePreview.value = imageUrl.value;
    };

    const onImageFileChange = (event: Event) => {
      const input = event.target as HTMLInputElement;
      const file = input.files?.[0];
      if (!file) return;

      if (!ALLOWED_IMAGE_TYPES.has(file.type)) {
        input.value = '';
        showError('Gambar tidak valid', 'Format gambar harus JPG atau PNG.');
        return;
      }

      if (file.size > MAX_IMAGE_SIZE_BYTES) {
        input.value = '';
        showError('Gambar terlalu besar', `Ukuran gambar maksimal ${MAX_IMAGE_SIZE_MB} MB.`);
        return;
      }

      formData.data.image = file;
      const reader = new FileReader();
      reader.onload = (loadEvent: ProgressEvent<FileReader>) => {
        imagePreview.value = String(loadEvent.target?.result || '');
      };
      reader.readAsDataURL(file);
    };

    const removeImage = () => {
      imageUrl.value = '';
      imagePreview.value = '';
      formData.data.image = '';
      imageMode.value = 'upload';
    };

    const handleSubmit = async () => {
      isLoading.value = true;

      const errors: string[] = [];

      const requiredFields: Record<string, string> = {
        name: 'Nama',
        stock: 'Stok',
        price: 'Harga',
        description: 'Deskripsi',
      };
      for (const [field, label] of Object.entries(requiredFields)) {
        const val = formData.data[field];
        if (val === undefined || val === null || val === '') {
          errors.push(`${label} wajib diisi.`);
        }
      }

      const stockValue = Number(formData.data.stock);
      if (formData.data.stock !== undefined && formData.data.stock !== '') {
        if (stockValue < 0) {
          errors.push('Stok tidak boleh bernilai negatif.');
        } else if (!Number.isInteger(stockValue)) {
          errors.push('Stok harus berupa bilangan bulat (tidak boleh menggunakan koma).');
        }
      }

      const hppValue = Number(formData.data.hpp || 0);
      if (formData.data.hpp !== undefined && formData.data.hpp !== '') {
        if (!Number.isFinite(hppValue) || hppValue < 0) {
          errors.push('HPP tidak boleh bernilai negatif.');
        }
      } else {
        formData.data.hpp = 0;
      }

      const link = typeof formData.data.link === 'string' ? formData.data.link : '';
      if (link && link.trim() !== '') {
        try {
          const url = new URL(link);
          const hostname = url.hostname.toLowerCase().replace(/\.$/, '');
          const isItbPressDomain = hostname === 'itbpress.id' || hostname.endsWith('.itbpress.id');
          if (!isItbPressDomain) {
            errors.push('Link harus menggunakan domain ITB Press (itbpress.id).');
          }
        } catch {
          errors.push('Masukkan URL yang benar (contoh: https://itbpress.id/...).');
        }
      }

      if (errors.length > 0) {
        showError('Error', errors);
        isLoading.value = false;
        return;
      }
      try {
        if(formData?.data?.image){
          if(typeof formData?.data?.image !== "string") {
            const response = await store.dispatch(POST_IMAGE, formData);
            formData.data.image = response || "";
          }
          formData.data.image = formData?.data?.image || "";
        }
        if(props?.id){
          formData.id = props?.id
          await store.dispatch(PUT_MERCHANDISE, formData);
        }else{
          await store.dispatch(POST_MERCHANDISE, formData);
        }
        closeModal();
      } catch (error: unknown) {
        const apiError = error as ApiErrorResponse;
        showError('Error', apiError?.response?.data?.message || apiError?.message || 'Terjadi kesalahan, coba lagi.');
        isLoading.value = false;
      }
    };

    return {
      modalContent,
      closeModal,
      handleSubmit,
      updateValue,
      isLoading,
      kategoriOptions,
      kategoriSelectOptions,
      selectedKategori,
      showNewKategori,
      newKategoriInput,
      onKategoriChange,
      formData,
      imageMode,
      imageUrl,
      imagePreview,
      formatCurrencyInput,
      onPriceInput,
      onHppInput,
      onImageUrlInput,
      onImageFileChange,
      removeImage,
    };
  },
});
